import * as THREE from 'three';
import { TextSprite } from '../TextSprite';
import { Utils } from '../utils';
import type { Viewer } from '../viewer/viewer';

type Rgba = { r: number; g: number; b: number; a: number };

/** Ground-plan lettering, like the room tags: light fill, the sprite's dark outline. */
const TEXT_COLOR: Rgba = { r: 245, g: 245, b: 245, a: 1 };
const SELECTED_TEXT_COLOR: Rgba = { r: 110, g: 231, b: 160, a: 1 };
const TRANSPARENT: Rgba = { r: 0, g: 0, b: 0, a: 0 };
const FONT_SIZE = 14;
/** Same screen size rule as the room tags. */
const SCREEN_SCALE = 70;
/** Gap between the line and the text, as a fraction of the text height. */
const LINE_GAP = 0.3;
/** The text is drawn at this many times its shown size, so it stays sharp (high-DPI screens). */
const OVERSAMPLE = 2;

/** How a dimension writes its length. */
export interface DimensionLabelStyle {
  decimals: number;
  showUnit: boolean;
  /** Turn the text with the line (kept readable); else it stays upright. */
  alongLine: boolean;
  /** Text scale (1 = normal). */
  size: number;
}

/**
 * The measured-length label of a dimension annotation: a screen-sized text
 * at the middle of the dimension line, turned with it (or upright), always
 * BESIDE the line on its screen-top side — far enough off that the text
 * never crosses it. Lives under the dimension's scene object (hidden with
 * it), in its local coordinates.
 */
export class DimensionLabel {
  private readonly sprite = new TextSprite('');
  /** Line middle (parent-local) and the line's world direction. */
  private readonly middle = new THREE.Vector3();
  private readonly direction = new THREE.Vector3(1, 0, 0);
  private style: DimensionLabelStyle = { decimals: 2, showUnit: true, alongLine: true, size: 1 };
  /** How many times bigger than FONT_SIZE the text texture is drawn (size × OVERSAMPLE). */
  private resolution = 0;
  private readonly onUpdate = (): void => this.place();

  constructor(
    private readonly viewer: Viewer,
    parent: THREE.Object3D,
  ) {
    this.applyResolution(1);
    this.sprite.setBorderColor(TRANSPARENT);
    this.sprite.setBackgroundColor(TRANSPARENT);
    this.sprite.setTextColor(TEXT_COLOR);
    this.sprite.material.depthTest = false;
    this.sprite.renderOrder = 5;
    parent.add(this.sprite);
    viewer.addEventListener('update', this.onUpdate);
  }

  /** Show `length` (m, world units) at `middle` (parent-local) of a line along `direction` (world). */
  set(length: number, style: DimensionLabelStyle, middle: { x: number; y: number; z: number }, direction: { x: number; y: number }): void {
    this.style = style;
    this.applyResolution(style.size);
    this.middle.set(middle.x, middle.y, middle.z);
    this.direction.set(direction.x, direction.y, 0).normalize();
    this.sprite.setText(this.format(length));
    this.place();
  }

  /** Draw the text for its shown size: a bigger text is drawn with a bigger font, not stretched (it would blur). */
  private applyResolution(size: number): void {
    const resolution = Math.max(0.25, size) * OVERSAMPLE;
    if (resolution === this.resolution) return;
    this.resolution = resolution;
    this.sprite.borderThickness = 2 * resolution;
    this.sprite.setFontsize(FONT_SIZE * resolution);
  }

  setSelected(selected: boolean): void {
    this.sprite.setTextColor(selected ? SELECTED_TEXT_COLOR : TEXT_COLOR);
  }

  private format(length: number): string {
    // Same conversion as the distance measure and room labels.
    let value = length;
    let suffix = 'm';
    const unit = this.viewer.lengthUnit;
    const display = this.viewer.lengthUnitDisplay;
    if (unit != null && display != null) {
      value = (length / unit.unitspermeter) * display.unitspermeter;
      suffix = display.code;
    }
    const digits = Math.max(0, Math.min(4, Math.round(this.style.decimals)));
    const text = Utils.addCommas(value.toFixed(digits));
    return this.style.showUnit ? `${text} ${suffix}` : text;
  }

  /** Constant screen size; turned with the line on screen; pushed off it toward the screen's top. */
  private place(): void {
    const parent = this.sprite.parent;
    if (!parent || !this.sprite.text) return;
    const camera = this.viewer.scene.getActiveCamera();
    const size = this.viewer.renderer.getSize(new THREE.Vector2());
    const middle = parent.localToWorld(this.middle.clone());
    const projected = Utils.projectedRadius(1, camera, camera.position.distanceTo(middle), size.width, size.height);
    // The texture is `resolution` times the base size: shrink by it, grow by the chosen size.
    const scale = ((SCREEN_SCALE / projected) * Math.max(0.25, this.style.size)) / this.resolution;
    this.sprite.scale.set(scale, scale, scale);

    // The line on screen (view space), turned into the readable half-turn.
    const onScreen = this.direction.clone().transformDirection(camera.matrixWorldInverse);
    let angle = Math.hypot(onScreen.x, onScreen.y) > 1e-6 ? Math.atan2(onScreen.y, onScreen.x) : 0;
    if (angle > Math.PI / 2) angle -= Math.PI;
    else if (angle <= -Math.PI / 2) angle += Math.PI;
    const rotation = this.style.alongLine ? angle : 0;
    this.sprite.setScreenRotation(rotation);

    // The line's normal on screen, on the text's top side (the screen's top for upright text).
    const normal = new THREE.Vector2(-Math.sin(angle), Math.cos(angle));
    // How far the text reaches across the line: its height turned with it, else its box seen across the line.
    const halfWidth = (scale * this.sprite.sprite.scale.x) / 2;
    const halfHeight = (scale * this.sprite.sprite.scale.y) / 2;
    const reach = this.style.alongLine ? halfHeight : halfWidth * Math.abs(normal.x) + halfHeight * Math.abs(normal.y);
    const offset = new THREE.Vector3(normal.x, normal.y, 0).applyQuaternion(camera.quaternion).multiplyScalar(reach + halfHeight * LINE_GAP);
    this.sprite.position.copy(parent.worldToLocal(middle.add(offset)));
  }

  dispose(): void {
    this.viewer.removeEventListener('update', this.onUpdate);
    this.sprite.removeFromParent();
    this.sprite.dispose();
  }
}
